
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { startCase } from "lodash";
import { Options, prop, Vue } from "vue-class-component";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../modules/cargo-module";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2
} from "@/data/payload/api/CargoV2ApiRequest";
import { DEFAULT_INTERNATIONAL_PRODUCT } from "../modules/constants";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  destinationCity = prop<OptionsClass>({
    default: () => null,
    type: Object
  });
}

@Options({
  emits: ["fetch-city", "update:destinationCity", "update:cargoType"],
  watch: {
    payloadTransportType: {
      handler(payload: any) {
        if (payload.origin && payload.destination) {
          this.fetchCargoTransportType();
        }
      },
      deep: true,
      immediate: true
    }
  }
})
class SectionSelectDestCargoType extends Vue.with(Props) {
  get form() {
    return CargoV2Controller.form;
  }

  isLoadingCargoType = false;
  cargoTypeOptions: OptionsClass[] = [];
  destinationCityOptions: OptionsClass[] = [];
  isLoadingDestinationCity = false;

  get payloadTransportType() {
    return {
      origin: this.form.originCityCode,
      destination: this.form.destinationCityCode.code || ""
    };
  }

  get destinationCityModel() {
    return this.destinationCity;
  }

  set destinationCityModel(value: OptionsClass) {
    this.$emit("update:destinationCity", value);
  }

  mapCargoTypeLabel(label: string) {
    const obj = {
      "Cargo Plane": "Pesawat",
      "Cargo Truck": "Truk",
      "Cargo Ship": "Kapal",
      "Cargo Train": "Kerate"
    } as any;

    const objEn = {
      "Cargo Plane": "Plane",
      "Cargo Truck": "Truck",
      "Cargo Ship": "Ship",
      "Cargo Train": "Train"
    } as any;

    const isId = this.$i18n.locale === "id";

    return isId ? obj[label] : objEn[label];
  }

  async fetchDestinationCity(search = "") {
    try {
      this.isLoadingDestinationCity = true;
      const resp = await LocationController.getDestinationCities({
        search,
        limit: 10,
        page: 1,
        status: ""
      });
      this.destinationCityOptions = resp.data.map((item: any) => {
        return new OptionsClass({
          id: item.id,
          label: `${item.code} - ${startCase(item.name?.toLowerCase())}`,
          code: item.code,
          name: startCase(item.name?.toLowerCase()),
          countryCode: item.country_code || ""
        });
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, this.$t("cargoV2.components.sectionSelectDestCargoType.error.getCity"), () =>
          this.fetchDestinationCity(search)
        )
      );
    } finally {
      this.isLoadingDestinationCity = false;
    }
  }

  async fetchCargoTransportType() {
    try {
      this.isLoadingCargoType = true;
      const resp = await CargoV2Controller.getCargoTransportType(
        this.payloadTransportType
      );
      this.cargoTypeOptions = resp
        .filter((ct) => ct.transportType !== "train")
        .map((ct) => {
          return new OptionsClass({
            label: this.mapCargoTypeLabel(ct.transportLabel),
            value: ct.transportType
          });
        });

      const cargoTypes = this.cargoTypeOptions.map((ct) => ct.value);
      if (
        this.form.cargoType.value &&
        !cargoTypes.includes(this.form.cargoType.value)
      ) {
        this.$emit("update:cargoType", new OptionsClass());
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, this.$t("cargoV2.components.sectionSelectDestCargoType.error.getCargoType"), () =>
          this.fetchCargoTransportType()
        )
      );
    } finally {
      this.isLoadingCargoType = false;
    }
  }

  async onSelectModa(value: OptionsClass | undefined) {
    if (!value) {
      this.form.cargoType = new OptionsClass();
      CargoV2Controller.setForm(this.form);
    }
    this.$nextTick(() => {
      CargoV2Controller.setForm({
        cargoNo: "",
        driverSelected: "",
        cargoVehicleNo: "",
        isValidVehicleNo: false,
        isErroAwb: false,
        isValidAwbManual: false
      });
      this.calculateVolume();
    })
  }

  async calculateVolume() {
    await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: CALCULATE_TYPE.CHANGE_MODA,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          currentGrossWeight: this.form.actualTotalCargoGrossweigth,
          height: this.form.actualDimension.cargoActualHeightDimension,
          length: this.form.actualDimension.cargoActualLengthDimension,
          width: this.form.actualDimension.cargoActualWidthDimension,
          totalPieces: this.form.cargoActualPiece,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable
        })
      })
    );
    await this.$nextTick();
    await this.$nextTick();
    await this.$nextTick();
    CargoV2Controller.setForm(this.form);
  }

  resetCargoTypes() {
    this.cargoTypeOptions = [];
    this.form.cargoType = new OptionsClass();;
    if (this.form.destinationCityCode.countryCode && this.form.destinationCityCode.countryCode !== "ID") {
      this.form.productCargo = DEFAULT_INTERNATIONAL_PRODUCT;
    } else if (!AccountController.accountData.accountIsForeign) {
      this.form.productCargo = "";
    }
    CargoV2Controller.setForm(this.form);
  }
}
export default SectionSelectDestCargoType;
